<script lang="ts" setup>
import countTo from 'vue-count-to'
import { computed, onUnmounted, ref, watch } from 'vue'
import type { MemberTopupSubscribeProduct, SubscribePageConfig } from '@/composables/payment'
import {
  AutoRenewalNotice,
  createShowPaymentMethodSelector,
  useSubscription,
} from '@/composables/payment'
import { PaymentMethodEnum, formatDiscountLabel } from '@/composables/payment/constants'
import { useAxonViewItem } from '@/composables/axon/useAxonViewItem'
import { useFbqAddToCart } from '@/composables/fbq/useFbqAddToCart'
import {
  getDailyPrice,
  getSavePercent,
  getUnitPriceValue,
  shouldShowDailyPrice,
} from '@/composables/payment/utils/subscribeDailyPrice'
import { VIP_TYPE } from '@/constants'
import { useGotoBack } from '@/composables'
import { resources as brandResources } from '@/config/brands'
import { useI18n } from '@/composables/useI18n'

const props = defineProps<{
  config: SubscribePageConfig
}>()

const { t } = useI18n()
const gotoBack = useGotoBack()

// ===== 订阅逻辑（与其他品牌完全一致，不做任何裁剪）=====
const {
  subscribeProducts,
  onSubscribeProducts,
  isZero,
  currentPaymentMethod,
  availablePaymentMethods,
  selectPaymentMethod,
} = useSubscription('store')

// 是否显示支付方式选择器（只有一种渠道时整块隐藏）
const showPaymentMethodSelector = createShowPaymentMethodSelector(availablePaymentMethods)

// Axon view_item 上报（商品加载 + 支付方式切换均触发，Set 内部去重）
const { reportViewItem } = useAxonViewItem()
watch(subscribeProducts, (val) => {
  if (val.length)
    reportViewItem(val, currentPaymentMethod.value, 'Membership', p => p.preferentialPrice ?? p.price)
})

const { reportAddToCart } = useFbqAddToCart()
watch(subscribeProducts, (val) => {
  for (const p of val)
    reportAddToCart(p.productId, 'Membership', p.preferentialPrice ?? p.price ?? 0)
})

const active = ref(0)

// 当前高亮套餐：仅从既有 active 索引派生（不引入任何新状态）；
// 索引指向的卡片不可见时（首屏 / 切换支付方式后列表变化）回落到第一张可见卡，保证高亮与底部 CTA 始终一致
const activeProduct = computed<any>(() => {
  const item: any = subscribeProducts.value[active.value]
  if (item?.isShow)
    return item

  return subscribeProducts.value.find((product: any) => product.isShow) ?? null
})

// 底部 CTA 仅作视觉元素：调用与点卡完全相同的支付函数，作用于当前 active 套餐
function onContinue() {
  if (activeProduct.value)
    onSubscribeProducts(activeProduct.value)
}

// CTA 实付金额：与卡片右侧价格取值口径保持一致
function getPayablePrice(item: any) {
  if (item.subscribeType === VIP_TYPE.DISCOUNT)
    return Number(item.preferentialPrice || item.price)

  if (!item.preferentialPrice)
    return Number(item.price)

  return Number(item.preferentialPrice)
}

// CTA 文案：Continue · $11.99
const ctaLabel = computed(() => {
  const item = activeProduct.value
  if (!item)
    return t('Continue')

  if (item.subscribeType !== VIP_TYPE.DISCOUNT && isZero(item.preferentialPrice))
    return `${t('Continue')} · ${props.config.texts.free}`

  const price = getPayablePrice(item)
  if (!Number.isFinite(price))
    return t('Continue')

  return `${t('Continue')} · $${price.toFixed(2)}`
})

// ===== 支付渠道（仅 PayPal / Stripe 两种，数据与切换函数沿用 useSubscription）=====
const paymentButtons = computed(() =>
  (availablePaymentMethods.value || []).map((method: any) => ({
    value: Number(method.paymentMethod),
    isPaypal: Number(method.paymentMethod) === PaymentMethodEnum.PAYPAL,
    discountLabel: formatDiscountLabel(method?.maxDiscountRate),
  })),
)

function isCurrentMethod(value: number) {
  return Number(currentPaymentMethod.value) === Number(value)
}

// ===== 折扣挽留弹窗（搬运 default 组件实现，逻辑一行不改）=====
const discountModel = ref(false)
const timer = ref<ReturnType<typeof setTimeout> | null>(null)
const currentDiscount: any = ref(null)
const discountNumber = ref(0)

function getDiscountPercentage() {
  if (subscribeProducts.value && subscribeProducts.value.length > 0) {
    let maxDiscount = 0
    let current = null
    subscribeProducts.value.forEach((item: MemberTopupSubscribeProduct) => {
      let discount = 0
      let shouldCalculate = false

      // 畅读会员：只有在 preferentialPrice 存在时才计算折扣(包括为0的情况)
      if (item.subscribeType === VIP_TYPE.ALL && (item.preferentialPrice !== undefined && item.preferentialPrice !== null)) {
        discount = (1 - (Number(item.preferentialPrice) / Number(item.price))) * 100
        shouldCalculate = true
      }

      // 折扣会员：有 discountOriginalPrice 就计算折扣
      if (item.subscribeType === VIP_TYPE.DISCOUNT && item.discountOriginalPrice) {
        discount = (1 - (Number(item.price) / Number(item.discountOriginalPrice))) * 100
        shouldCalculate = true
      }

      // 只有符合条件的会员才参与比较
      if (shouldCalculate && discount >= maxDiscount) {
        current = item
        maxDiscount = Math.round(discount)
      }
    })

    // 只要找到符合条件的会员就返回，不管折扣是多少(包括0)
    if (current)
      return [current, maxDiscount]
  }

  return false
}

function initDiscountModel() {
  const showDiscount = window.localStorage.getItem('showDiscount')
  if (showDiscount)
    return

  timer.value = setTimeout(() => {
    const discountResult = getDiscountPercentage()
    discountNumber.value = discountResult ? (discountResult[1] ?? 0) : 0
    currentDiscount.value = discountResult ? discountResult[0] : null

    if (!Array.isArray(discountResult)) {
      clearTimeout(timer.value!)
      return
    }

    discountModel.value = true
    window.localStorage.setItem('showDiscount', '1')
  }, 6000)
}
initDiscountModel()

function buyNow() {
  onSubscribeProducts(currentDiscount.value)
}

onUnmounted(() => {
  if (timer.value)
    clearTimeout(timer.value)
})
</script>

<template>
  <div class="nm-page">
    <!-- 顶部返回（本页不使用 BasicNavbar，与设计稿的内联返回箭头一致） -->
    <div class="nm-header">
      <button type="button" class="nm-back" aria-label="Back" @click="gotoBack()">
        <i-lucide-chevron-left class="nm-back-icon" />
      </button>
    </div>

    <!-- 标题区 -->
    <div class="nm-hero">
      <div class="nm-title-row">
        <i-lucide-crown class="nm-crown" />
        <h1 class="nm-title">
          {{ config.texts.vipMembership }}
        </h1>
      </div>
      <p class="nm-subtitle">
        {{ config.texts.membershipDesc }}
      </p>
      <p class="nm-subtitle">
        {{ config.texts.notJoinedYet }}
      </p>
    </div>

    <!-- 套餐卡列表 -->
    <div class="nm-plans">
      <div v-for="(item, index) in subscribeProducts" :key="index">
        <div
          v-if="item.isShow"
          class="nm-card"
          :class="{
            'is-active': item === activeProduct,
            'is-coins': item.subscribeType === VIP_TYPE.DISCOUNT,
          }"
          @click="() => { active = index; onSubscribeProducts(item); }"
        >
          <!-- 角标（运营配置驱动，两种会员都可能有） -->
          <div v-if="item.cornerTip === 1 && item.marketingCopy" class="nm-chip">
            {{ item.marketingCopy }}
          </div>

          <!-- 左侧信息 -->
          <div class="nm-card-left">
            <!-- 折扣会员（金币套餐） -->
            <template v-if="item.subscribeType === VIP_TYPE.DISCOUNT">
              <div class="nm-coins-row">
                <img class="nm-coins-icon" :src="brandResources.coin">
                <span class="nm-coins-amount">{{ item.discountCoins }} {{ t('coins') }}</span>
                <span v-if="item.cycleStr" class="nm-cycle-tag">{{ item.cycleStr }}</span>
              </div>
              <p class="nm-card-desc">
                {{ config.texts.payTemplateGetCoins?.(item.discountOnceCoins) }}
              </p>
              <p class="nm-card-desc">
                {{ config.texts.payTemplateEarnCoins?.(item.discountBonusCoins, item.discountDailyCoins) }}
              </p>
            </template>

            <!-- 畅读会员 -->
            <template v-else>
              <div class="nm-name-row">
                <BasicFitText class="nm-card-name">
                  {{ item.cycleStr }}
                </BasicFitText>
                <span v-if="shouldShowDailyPrice(item, isZero)" class="nm-save-tag">
                  SAVE {{ getSavePercent(item) }}%
                </span>
              </div>
              <!-- 覆写文案(ZKSN-251):一行放得下则单行展示,放不下按 \n 拆行并逐行缩放 -->
              <div class="nm-card-desc">
                <BasicFitLines v-if="item.subscribeTextOverridden" :text="item.subscribeText" />
                <template v-else>
                  {{ item.subscribeText }}
                </template>
              </div>
            </template>
          </div>

          <!-- 右侧价格 -->
          <div class="nm-card-right">
            <div class="nm-price">
              <template v-if="item.subscribeType === VIP_TYPE.DISCOUNT">
                <span class="price-counter">
                  <span aria-hidden="true" class="price-counter__holder">$ {{ Number(item.discountOriginalPrice).toFixed(2) }}</span>
                  <span class="price-counter__value">$<countTo
                    :start-val="Number(item.discountOriginalPrice)"
                    :end-val="Number(item.preferentialPrice || item.price)"
                    :duration="3000"
                    :decimals="2"
                  /></span>
                </span>
              </template>
              <template v-else-if="isZero(item.preferentialPrice)">
                {{ config.texts.free }}
              </template>
              <template v-else-if="!item.preferentialPrice">
                $ {{ item.price }}
              </template>
              <template v-else>
                <span class="price-counter">
                  <span aria-hidden="true" class="price-counter__holder">$ {{ Number(item.price).toFixed(2) }}</span>
                  <span class="price-counter__value">$<countTo
                    :start-val="Number(item.price)"
                    :end-val="Number(item.preferentialPrice)"
                    :duration="3000"
                    :decimals="2"
                  /></span>
                </span>
              </template>
            </div>

            <div class="nm-price-sub">
              <template v-if="item.subscribeType === VIP_TYPE.DISCOUNT">
                <span class="nm-line-through">$ {{ item.discountOriginalPrice }}</span>
              </template>
              <template v-else-if="isZero(item.preferentialPrice)">
                <span class="nm-line-through">$ {{ item.price }}</span>
              </template>
              <template v-else-if="!item.preferentialPrice">
                <span v-if="getUnitPriceValue(item)">$ {{ getUnitPriceValue(item) }} / Day</span>
              </template>
              <template v-else>
                <span class="nm-line-through">$ {{ item.price }}</span>
                <span v-if="shouldShowDailyPrice(item, isZero)"> · $ {{ getDailyPrice(item) }} / Day</span>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Tips -->
    <div class="nm-tips">
      <p class="nm-tips-title">
        {{ config.texts.tips }}
      </p>
      <p class="nm-tips-item">
        {{ config.texts.tip1 }}
      </p>
      <p class="nm-tips-item">
        {{ config.texts.tip2 }}
      </p>
      <!-- 自动续费提示(仅自然用户展示;订阅中心无充值商品,不含括号内容) -->
      <AutoRenewalNotice class="nm-tips-item" has-subscribe :has-topup="false" prefix="3. " />
    </div>

    <!-- 底部：支付渠道 + CTA -->
    <div class="nm-footer">
      <div v-if="showPaymentMethodSelector" class="nm-pay-row">
        <button
          v-for="method in paymentButtons"
          :key="method.value"
          type="button"
          class="nm-pay-btn"
          :class="{ 'is-active': isCurrentMethod(method.value) }"
          @click="selectPaymentMethod(method.value)"
        >
          <!-- PayPal -->
          <template v-if="method.isPaypal">
            <i-lucide-circle-dollar-sign class="nm-pay-icon-16" />
            <span class="nm-pay-label">PayPal</span>
          </template>
          <!-- 聚合（Stripe）：Apple / Google / 银行卡 -->
          <template v-else>
            <i-lucide-apple class="nm-pay-icon-18" />
            <i-ph-google-logo class="nm-pay-icon-18" />
            <i-lucide-credit-card class="nm-pay-icon-18" />
          </template>

          <span v-if="method.discountLabel" class="nm-pay-discount">{{ method.discountLabel }}</span>
        </button>
      </div>

      <button type="button" class="nm-cta" @click="onContinue">
        {{ ctaLabel }}
      </button>
    </div>

    <!-- 折扣挽留弹窗 -->
    <BasicModal v-model="discountModel" :transparent="true" class-name="discount-modal">
      <!-- 此容器不能加 relative：会变成定位元素并按 DOM 顺序盖住前面的关闭按钮；
           内部叠加文案锚定外层同宽的弹窗容器即可 -->
      <div @click="buyNow">
        <div class="w-[200px] flex justify-center absolute top-[40px] left-[50%] ml-[-100px]">
          <div class="text-white text-30px font-bold italic text-center">
            {{ discountNumber }}<span class="text-20px">%</span>
            <div class="relative top-[-5px]">
              OFF
            </div>
          </div>
        </div>
        <!-- 图片必须跟弹窗容器同一套 rem 缩放基准(w-full)；HTML width 属性是固定 CSS 像素，
             非 375 视口下会与 w-300px 容器错位，导致叠加文案/关闭按钮偏移 -->
        <img :src="config.resources.discountBg" class="w-full block">
      </div>
    </BasicModal>
  </div>
</template>

<style scoped>
/* NovelMello 订阅页为设计稿指定的深紫整页方案，色值随组件落地（品牌支付配置的 subscribe 段为浅色通用方案，不在此复用） */
.nm-page {
  --nm-font-display: 'Baloo 2', 'Nunito Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  --nm-font-body: 'Nunito Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  --nm-page-bg: #1D1233;
  --nm-card-bg: #2B1B45;
  --nm-card-border: rgba(139, 92, 246, 0.3);
  --nm-coins-card-border: rgba(255, 178, 62, 0.35);
  --nm-highlight-gradient: linear-gradient(120deg, #F0509B 0%, #9B5CF0 100%);
  --nm-highlight-shadow: 0 8px 24px rgba(240, 80, 155, 0.3);
  --nm-price-color: #FFB9DC;
  --nm-gold: #FFB23E;
  --nm-pink: #F0509B;
  --nm-cta-gradient: linear-gradient(90deg, #F0509B 0%, #9B5CF0 100%);
  --nm-cta-shadow: 0 8px 24px rgba(240, 80, 155, 0.35);

  position: relative;
  min-height: 100vh;
  padding: 0 16px calc(190px + env(safe-area-inset-bottom));
  background: #1D1233;
  font-family: var(--nm-font-body);
  box-sizing: border-box;
}

/* 顶部返回 */
.nm-header {
  padding-top: 16px;
}

.nm-back {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin-left: -6px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #FFFFFF;
}

.nm-back-icon {
  width: 24px;
  height: 24px;
}

/* 标题区 */
.nm-hero {
  margin-top: 8px;
}

.nm-title-row {
  display: flex;
  align-items: center;
}

.nm-crown {
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  margin-right: 10px;
  color: var(--nm-gold);
}

.nm-title {
  margin: 0;
  font-family: var(--nm-font-display);
  font-size: 26px;
  font-weight: 700;
  line-height: 1.2;
  color: #FFFFFF;
}

.nm-subtitle {
  margin: 8px 0 0;
  font-size: 13px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.65);
}

/* 套餐卡 */
.nm-plans {
  margin-top: 20px;
}

.nm-card {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 16px 15px;
  margin-bottom: 12px;
  border-radius: 18px;
  border: 1px solid var(--nm-card-border);
  background: var(--nm-card-bg);
  cursor: pointer;
  transition: box-shadow 0.2s ease, background 0.2s ease;
  box-sizing: border-box;
}

/* 金币套餐卡：金色描边 */
.nm-card.is-coins {
  border-color: var(--nm-coins-card-border);
}

/* 选中卡：粉紫渐变 + 阴影 */
.nm-card.is-active {
  border-color: transparent;
  background: var(--nm-highlight-gradient);
  box-shadow: var(--nm-highlight-shadow);
}

/* 角标 */
.nm-chip {
  position: absolute;
  top: -8px;
  left: 16px;
  height: 16px;
  padding: 0 8px;
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  background: #FFFFFF;
  color: var(--nm-pink);
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
}

.nm-card-left {
  flex: 1;
  /* 允许被压缩:否则 nowrap 的自适应文案(BasicFitLines)会撑宽左列，把价格区挤出卡片 */
  min-width: 0;
}

.nm-card-right {
  flex-shrink: 0;
  margin-left: 12px;
  text-align: right;
}

/* 名称 / 描述 */
.nm-name-row {
  display: flex;
  align-items: center;
  min-width: 0;
}

.nm-card-name {
  font-family: var(--nm-font-display);
  font-size: 18px;
  font-weight: 700;
  line-height: 1.2;
  color: #FFFFFF;
  white-space: nowrap;
}

.nm-card.is-active .nm-card-name {
  font-size: 20px;
}

/* 折扣会员卡的订阅周期标签与 SAVE 标签同款式 */
.nm-save-tag,
.nm-cycle-tag {
  flex-shrink: 0;
  margin-left: 6px;
  padding: 3px 6px 4px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.18);
  color: #FFFFFF;
  font-size: 10px;
  font-weight: 700;
  line-height: 8px;
  white-space: nowrap;
}

.nm-card-desc {
  margin: 6px 0 0;
  font-size: 12px;
  line-height: 1.4;
  color: rgba(255, 255, 255, 0.55);
  overflow: hidden;
  text-overflow: ellipsis;
}

.nm-card.is-active .nm-card-desc {
  color: rgba(255, 255, 255, 0.8);
}

/* 金币行 */
.nm-coins-row {
  display: flex;
  align-items: center;
}

.nm-coins-icon {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  margin-right: 8px;
}

.nm-coins-amount {
  font-family: var(--nm-font-display);
  font-size: 18px;
  font-weight: 700;
  line-height: 1.2;
  color: var(--nm-gold);
  white-space: nowrap;
}

.nm-card.is-active .nm-coins-amount {
  color: #FFFFFF;
}

.nm-card.is-coins .nm-card-desc {
  font-size: 11px;
}

/* 价格 */
.nm-price {
  font-family: var(--nm-font-display);
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
  color: var(--nm-price-color);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.nm-card.is-active .nm-price {
  font-size: 22px;
  color: #FFFFFF;
}

.nm-price-sub {
  margin-top: 4px;
  font-size: 11px;
  line-height: 1.3;
  color: rgba(255, 255, 255, 0.55);
  white-space: nowrap;
}

.nm-card.is-active .nm-price-sub {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
}

.nm-line-through {
  text-decoration: line-through;
}

/* 价格倒数动画：用不可见的原价占位撑出固定宽度，动画数字绝对定位叠加其上，
   避免 countTo 逐帧改变数字宽度导致卡片内容左右抖动 */
.price-counter {
  position: relative;
  display: inline-block;
  white-space: nowrap;
}

.price-counter__holder {
  visibility: hidden;
}

.price-counter__value {
  position: absolute;
  top: 0;
  right: 0;
  white-space: nowrap;
}

/* Tips */
.nm-tips {
  margin-top: 20px;
}

.nm-tips-title {
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.5);
}

.nm-tips-item {
  margin: 6px 0 0;
  font-size: 11px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.4);
}

.nm-tips-item :deep(a) {
  color: rgba(255, 255, 255, 0.6);
}

/* 底部固定条 */
.nm-footer {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 5;
  padding: 20px 16px calc(16px + env(safe-area-inset-bottom));
  /* 渐隐遮罩，避免列表内容被硬切 */
  background: linear-gradient(180deg, rgba(29, 18, 51, 0) 0%, #1D1233 28%, #1D1233 100%);
  box-sizing: border-box;
}

/* stylelint-disable-next-line unit-case */
@media (min-width: 768px) {
  .nm-footer {
    left: 50%;
    right: auto;
    width: 750PX;
    max-width: 750PX;
    transform: translateX(-50%);
  }
}

.nm-pay-row {
  display: flex;
  margin-bottom: 12px;
}

.nm-pay-btn {
  position: relative;
  flex: 1;
  min-width: 0;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 10px;
  border-radius: 22px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.08);
  color: #FFFFFF;
  transition: border-color 0.2s ease, background 0.2s ease;
  box-sizing: border-box;
}

/* 两个按钮之间 12px 间距（不用 flex gap，兼容 iOS 14.4 以下机型）*/
.nm-pay-btn + .nm-pay-btn {
  margin-left: 12px;
}

.nm-pay-btn.is-active {
  border-color: var(--nm-pink);
  background: rgba(240, 80, 155, 0.18);
}

.nm-pay-icon-16 {
  width: 16px;
  height: 16px;
  margin-right: 6px;
}

.nm-pay-icon-18 {
  width: 18px;
  height: 18px;
}

/* 聚合按钮三图标间距 10px */
.nm-pay-icon-18 + .nm-pay-icon-18 {
  margin-left: 10px;
}

.nm-pay-label {
  font-size: 12px;
  font-weight: 700;
  color: #FFFFFF;
}

.nm-pay-discount {
  position: absolute;
  top: -8px;
  left: 10px;
  padding: 0 6px;
  border-radius: 999px;
  background: var(--nm-cta-gradient);
  color: #FFFFFF;
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  white-space: nowrap;
}

/* CTA */
.nm-cta {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 50px;
  border: 0;
  border-radius: 25px;
  background: var(--nm-cta-gradient);
  box-shadow: var(--nm-cta-shadow);
  color: #FFFFFF;
  font-size: 16px;
  font-weight: 800;
  letter-spacing: 0.01em;
}

.nm-cta:active {
  transform: translateY(1px);
}
</style>

<style lang="scss">
.discount-modal {
  .close-icon {
    top: 12px;
    right: 0;
  }
}
</style>
